import { toast } from "@/components/ui/toast"
import { clsx, type ClassValue } from "clsx"
import { Check, CircleAlert } from "lucide-react"
import { twMerge } from "tailwind-merge"

const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error(error)
  }
};

const scroll = (id: string) => {
  const container = document.getElementById("main-content-area");
  const target = document.getElementById(id);
  if (container && target) {
    const scrollOffset = target.offsetTop - container.offsetTop - 0;
    container.scrollTo({ top: scrollOffset, behavior: "smooth" });
  }
}

const jump = (id: string) => {
  let container = document.getElementById("main-content-area");
  let target = document.getElementById(id);
  if (container && target) {
    const scrollOffset = target.offsetTop - container.offsetTop - 144;
    container.scrollTo({ top: scrollOffset, behavior: "auto" });
  }
  container = document.getElementById("sidebar-scrollable-container");
  target = document.getElementById("sidebar_" + id);
  if (container && target) {
    const scrollOffset = target.offsetTop - container.offsetTop - 32;
    container.scrollTo({ top: scrollOffset, behavior: "auto" });
  }
}

const formatDate = (dateString: string) => {
  // 1. Guard against empty or null strings
  if (!dateString) return "N/A";

  const date = new Date(dateString);

  // 2. Check if the date object is valid
  // isNaN(date.getTime()) returns true if the date is "Invalid Date"
  if (isNaN(date.getTime())) {
    console.error(`Invalid date provided: ${dateString}`);
    return "Invalid Date";
  }

  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC", // Forces consistency between server and client
  }).format(date);
};

async function copyToClipboard(text: string): Promise<void> {

  if (!navigator.clipboard) {
    // Fallback for older browsers or non-secure contexts
    return fallbackCopyToClipboard(text);
  }

  try {
    toast({
      text: 'Copied to clipboard.',
      icon: Check,
      // description: 'You can paste the description anywere.'
    })
    await navigator.clipboard.writeText(text);
  } catch (err) {
    toast({
      text: 'Failed to copy.',
      icon: CircleAlert
    })
    throw err;
  }
}

function fallbackCopyToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  
  // Ensure the textarea is not visible but stays in the DOM
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  textArea.style.top = "0";
  document.body.appendChild(textArea);
  
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (!successful) throw new Error('Fallback copy failed');
  } catch (err) {
    console.error('Fallback: Unable to copy', err);
  }

  document.body.removeChild(textArea);
}

function cosineSimilarity(vecA: number[], vecB: number[]) {
  let dotProduct = 0;
  let mA = 0;
  let mB = 0;

  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    mA += vecA[i] * vecA[i];
    mB += vecB[i] * vecB[i];
  }
  
  const similarity = dotProduct / (Math.sqrt(mA) * Math.sqrt(mB));
  return similarity || 0; // Handle division by zero
}


const downloadFile = (content: string, fileName: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export {
  cn,
  capitalize,
  copy,
  scroll,
  jump,
  formatDate,
  copyToClipboard,
  cosineSimilarity,
  downloadFile
}
